import type {
  C3Campaign,
  C3Entity,
  C3FlowCounts,
  C3MockIncident,
  C3Rank,
} from './types';

export const C3_INSIGHTS = [
  {
    cat: 'Cross-Fleet Pattern',
    text: 'Three Okta users show anomaly patterns matching early-stage credential compromise across the fleet.',
  },
  {
    cat: 'Policy Tuning',
    text: 'Two thresholds you set 30 days ago trigger 6x more false positives than fleet median. Consider tuning.',
  },
  {
    cat: 'Risk Evolution',
    text: 'Emerging risk concentration on three crown-jewel assets in the AWS prod account this week.',
  },
  {
    cat: 'FP Reduction',
    text: '693 false positives suppressed today at 99.1% precision (2 reopened in 30 days). Analyst pages ↓ 87% vs. pre-deployment baseline.',
  },
];

export const DEFAULT_RANKS: C3Rank[] = [
  { name: 'Revoke Okta session / force re-auth', desc: 'Identity plane · reversible in seconds', risk: 'low', mode: 'auto' },
  { name: 'Revoke OAuth grants & quarantine apps', desc: 'Identity plane · reversible', risk: 'low', mode: 'auto' },
  { name: 'Isolate engineering endpoints', desc: 'Graduated 6/30 after 14 validated isolations', risk: 'low', mode: 'auto' },
  { name: 'Revoke AWS STS / access keys', desc: 'Cloud plane · reversible via re-issue', risk: 'med', mode: 'auto' },
  { name: 'Kill process & quarantine files', desc: 'Endpoint plane · reversible', risk: 'low', mode: 'auto' },
  { name: 'Isolate HR / exec / finance endpoints', desc: 'Business-disruption policy', risk: 'med', mode: 'gated' },
  { name: 'Modify prod security groups & IAM policies', desc: 'Cloud plane · prod blast radius', risk: 'high', mode: 'gated' },
  { name: 'Freeze employee identity (full suspend)', desc: 'Identity plane · HR-sensitive', risk: 'high', mode: 'gated' },
];

export const DEFAULT_FLOW: C3FlowCounts = {
  raw: 12847,
  dedup: 2312,
  baseline: 8564,
  anomalies: 1204,
  threats: 205,
  fp: 693,
  esc: 73,
  incidents: 47,
  autoHandled: 42,
  humanQueue: 5,
};

export const C3_ENTITIES: C3Entity[] = [
  {
    n: 'j.chen',
    type: 'Identity · Engineering',
    score: 92,
    comps: [95, 88, 90, 72],
    trend: '+31',
    rel: ['INC-4021', 'INC-4014'],
    brief:
      'Compromised identity at the center of today’s critical kill-chain. Credential theft in Lagos, cloud session abuse via STS, and endpoint C2 persistence were joined on this principal inside a 9-minute window. Sessions revoked, tokens invalidated, device containment in progress. Recommend credential reset ceremony + 30-day step-up MFA.',
    tl: [
      ['08:02', 'Okta impossible-travel sign-in (NYC → Lagos)'],
      ['08:07', 'STS calls from unseen ASN — GetSecretValue attempts'],
      ['08:11', 'C2 beacon on assigned device eng-laptop-114'],
      ['08:11', 'Auto-remediation: sessions + tokens revoked'],
      ['08:12', 'Containment requested on device'],
    ],
  },
  {
    n: 'm.ortiz',
    type: 'Identity · Database admin',
    score: 88,
    comps: [92, 90, 55, 68],
    trend: '+24',
    rel: ['INC-4025'],
    brief:
      'Self-elevation pattern across identity planes: granted AdministratorAccess to dba-role, mirrored in Okta group membership, then touched privileged host db-bastion-02 — all outside change windows. Remediation is approval-gated (employee identity freeze). Awaiting SOC decision, SLA 15m.',
    tl: [
      ['09:14', 'iam:AttachRolePolicy → AdministratorAccess on dba-role'],
      ['09:16', 'Added self to AWS-Prod-Admins in Okta'],
      ['09:19', 'Interactive session on db-bastion-02 (first in 90d)'],
      ['09:19', 'Freeze plan synthesized — GATED, approval pending'],
    ],
  },
  {
    n: 'eng-laptop-114',
    type: 'Endpoint · Engineering fleet',
    score: 81,
    comps: [60, 35, 96, 70],
    trend: '+18',
    rel: ['INC-4021', 'INC-4014'],
    brief:
      'Assigned to j.chen. Beacon cadence to an uncategorized domain (60s interval, unsigned parent) plus involvement in the INC-4021 kill-chain. CrowdStrike containment in progress; triage package captured. Re-image recommended before release from containment.',
    tl: [
      ['04:18', 'Beacon detected → auto-isolated per Rank policy'],
      ['08:11', 'Linked into INC-4021 correlation'],
      ['08:12', 'Network containment requested'],
    ],
  },
  {
    n: 'prod-account',
    type: 'Cloud · AWS production account',
    score: 77,
    comps: [52, 94, 20, 88],
    trend: '+9',
    rel: ['INC-4022', 'INC-4025'],
    brief:
      'Crown-jewel AWS account. IAM drift from Terraform baseline (inline iam:* grant) and the dba-role escalation both touch this account. Two gated remediations pending. Exposure component elevated by open approvals.',
    tl: [
      ['09:14', 'dba-role escalation touches account'],
      ['09:30', 'Inline policy drift vs IaC baseline detected'],
      ['09:30', 'Rollback plan GATED — awaiting approval'],
    ],
  },
  {
    n: 'data-lake',
    type: 'Cloud · S3 data store',
    score: 74,
    comps: [45, 90, 15, 85],
    trend: '+12',
    rel: ['INC-4030'],
    brief:
      'Target of token-based exfil pattern via contractor OAuth grant. Deny policy applied to tagged sessions; access-log snapshot in progress for the IR report. No confirmed data egress beyond anomaly threshold.',
    tl: [
      ['07:48', 'Contractor OAuth grant to unverified app'],
      ['07:59', 'GuardDuty anomalous GetObject volume'],
      ['07:59', 'Grant revoked · deny policy applied'],
    ],
  },
  {
    n: 'dba-role',
    type: 'Cloud · IAM role',
    score: 69,
    comps: [70, 88, 10, 60],
    trend: '+21',
    rel: ['INC-4025'],
    brief:
      'Received AdministratorAccess outside change window during the m.ortiz escalation chain. Detach is pending approval; role remains under continuous watch with all uses logged to the audit trail.',
    tl: [
      ['09:14', 'AdministratorAccess attached by m.ortiz'],
      ['09:19', 'Flagged in escalation chain'],
      ['09:19', 'Detach plan pending approval'],
    ],
  },
  {
    n: 'fin-laptop-registry',
    type: 'Endpoint · Finance',
    score: 66,
    comps: [72, 58, 80, 40],
    trend: '+15',
    rel: ['INC-4033'],
    brief:
      'Entry point of the phish-to-cloud pivot: blocked macro, MFA push-bombing, then rogue role creation in the payments account. Host contained, sessions revoked; rogue-role deletion gated by payments-account policy.',
    tl: [
      ['10:22', 'Malicious macro → PowerShell (blocked)'],
      ['10:24', 'MFA push-bombing, one approved'],
      ['10:31', 'Rogue role in payments account'],
      ['10:32', 'Host contained · role deletion GATED'],
    ],
  },
  {
    n: 'hr-laptop-032',
    type: 'Endpoint · HR',
    score: 58,
    comps: [40, 20, 84, 45],
    trend: '−4',
    rel: ['INC-4017'],
    brief:
      'Office-macro spawn blocked by sensor; process tree killed and file quarantined. Full isolation gated under the HR business-disruption policy. Residual persistence risk assessed as low but non-zero.',
    tl: [
      ['09:02', 'WINWORD → powershell blocked'],
      ['09:02', 'Process tree killed, file quarantined'],
      ['09:02', 'Isolation GATED — approval pending'],
    ],
  },
];

export const C3_CAMPAIGNS: C3Campaign[] = [
  {
    id: 'CAMP-01',
    name: 'Credential compromise kill-chain',
    sev: 'critical',
    sevc: 'sev-critical',
    members: ['INC-4021', 'INC-4014'],
    alerts: 14,
    state: 'In progress',
    statec: 'state-blue',
    summary:
      'Identity-driven kill-chain: credential theft → cloud session abuse → endpoint persistence. Containment in flight on eng-laptop-114.',
  },
  {
    id: 'CAMP-02',
    name: 'Cross-plane privilege escalation',
    sev: 'critical',
    sevc: 'sev-critical',
    members: ['INC-4025', 'INC-4022'],
    alerts: 9,
    state: 'Awaiting decision',
    statec: 'state-amber',
    summary:
      'Self-elevation across AWS IAM and Okta converging on prod-account. Gated per Agent Rank for identity-freeze.',
  },
  {
    id: 'CAMP-03',
    name: 'OAuth → S3 exfil',
    sev: 'high',
    sevc: 'sev-high',
    members: ['INC-4030'],
    alerts: 6,
    state: 'In progress',
    statec: 'state-blue',
    summary: 'Token-lineage exfil against data-lake; grant revoked, deny policy applied, snapshot in progress.',
  },
];

export const CAMP_OF: Record<string, string> = {
  'INC-4021': 'CAMP-01',
  'INC-4014': 'CAMP-01',
  'INC-4025': 'CAMP-02',
  'INC-4022': 'CAMP-02',
  'INC-4030': 'CAMP-03',
};

export const C3_BRIEF_HTML = `
<p>Overnight, Arbitium absorbed a quiet but coordinated identity campaign. The lead thread is
<span class="cite" data-inc="INC-4021">INC-4021</span> — credential compromise spanning Okta, AWS, and CrowdStrike —
now mid-containment with two identity-plane freezes complete and host isolation in progress.</p>
<p>Two approvals sit in your queue:
<span class="cite" data-inc="INC-4025">INC-4025</span> (cross-plane privilege escalation) and
<span class="cite" data-inc="INC-4033">INC-4033</span> (phish → cloud pivot). Both are gated by Agent Rank policy.</p>
<p>Also notable: token-lineage exfil against the data-lake was cut off mid-pattern
<span class="cite" data-inc="INC-4030">INC-4030</span>, and one containment was self-reverted after validation showed a benign registry health-check
<span class="cite" data-inc="INC-4001">INC-4001</span> — 94 seconds of impact, pattern learned.</p>
<p>Recommended focus: clear the two gated approvals, then review Agent Rank graduation candidates.</p>
`;

export const CG_NODES = [
  { id: 'INC-4021', kind: 'inc' as const, x: 0.35, y: 0.3, label: 'INC-4021' },
  { id: 'INC-4025', kind: 'inc' as const, x: 0.65, y: 0.25, label: 'INC-4025' },
  { id: 'INC-4030', kind: 'inc' as const, x: 0.55, y: 0.55, label: 'INC-4030' },
  { id: 'INC-4033', kind: 'inc' as const, x: 0.25, y: 0.6, label: 'INC-4033' },
  { id: 'j.chen', kind: 'ent' as const, x: 0.2, y: 0.35, label: 'j.chen' },
  { id: 'm.ortiz', kind: 'ent' as const, x: 0.8, y: 0.35, label: 'm.ortiz' },
  { id: 'data-lake', kind: 'ent' as const, x: 0.7, y: 0.7, label: 'data-lake' },
  { id: 'Okta', kind: 'src' as const, x: 0.1, y: 0.15, label: 'Okta' },
  { id: 'AWS', kind: 'src' as const, x: 0.9, y: 0.15, label: 'AWS' },
  { id: 'CrowdStrike', kind: 'src' as const, x: 0.5, y: 0.85, label: 'CrowdStrike' },
];

export const CG_EDGES: [string, string][] = [
  ['Okta', 'INC-4021'],
  ['AWS', 'INC-4021'],
  ['CrowdStrike', 'INC-4021'],
  ['j.chen', 'INC-4021'],
  ['AWS', 'INC-4025'],
  ['Okta', 'INC-4025'],
  ['m.ortiz', 'INC-4025'],
  ['Okta', 'INC-4030'],
  ['AWS', 'INC-4030'],
  ['data-lake', 'INC-4030'],
  ['CrowdStrike', 'INC-4033'],
  ['Okta', 'INC-4033'],
];

export const GEO_SITES = [
  { name: 'NYC', x: 0.28, y: 0.38, count: 12 },
  { name: 'Lagos', x: 0.52, y: 0.55, count: 3 },
  { name: 'Frankfurt', x: 0.53, y: 0.32, count: 5 },
  { name: 'Singapore', x: 0.78, y: 0.55, count: 4 },
  { name: 'São Paulo', x: 0.35, y: 0.68, count: 2 },
];

function heroIncidents(): C3MockIncident[] {
  return [
    {
      id: 'INC-4021',
      sev: 'critical',
      title: 'Credential compromise → lateral movement',
      source: 'Okta → AWS → CrowdStrike',
      asset: 'prod-iam / crown-jewel',
      state: 'progress',
      xdom: true,
      detail: {
        signals: [
          ['08:02:14', 'Okta', 'Impossible-travel sign-in: j.chen@ NYC → Lagos, 8 min apart. New device fingerprint.'],
          ['08:07:41', 'AWS CloudTrail', 'STS session for j.chen issuing API calls from AS37963. ListBuckets, GetSecretValue attempts.'],
          ['08:11:03', 'CrowdStrike', 'C2-pattern beacon from eng-laptop-114 to uncategorized domain, 60s interval.'],
        ],
        reasoning:
          'Three medium-severity signals from three separate tools, joined on identity j.chen within a 9-minute window. Individually each is ambiguous; correlated, they match an identity-driven kill-chain.',
        confidence: 0.94,
        attack: ['T1078 Valid Accounts', 'T1021 Remote Services', 'T1071 C2 Channel'],
        chain: [
          ['Disable Okta user + revoke all sessions', 'done'],
          ['Revoke AWS STS tokens for principal j.chen', 'done'],
          ['Isolate host eng-laptop-114 (CrowdStrike containment)', 'progress'],
        ],
        audit: [
          ['08:11:07', 'ai', 'Correlation engine joined 3 signals on identity=j.chen (conf 0.94)'],
          ['08:11:09', 'ai', 'Remediation plan synthesized: 3-step identity-plane freeze'],
          ['08:11:11', 'ok', 'Okta: user j.chen suspended, 4 active sessions revoked'],
          ['08:11:13', 'ok', 'AWS: STS tokens invalidated via policy attach'],
          ['08:11:15', 'ai', 'CrowdStrike: network containment requested — in progress'],
        ],
      },
    },
    {
      id: 'INC-4025',
      sev: 'critical',
      title: 'Privilege-escalation chain across identity planes',
      source: 'AWS → Okta → CrowdStrike',
      asset: 'prod-account / dba-role',
      state: 'awaiting',
      xdom: true,
      detail: {
        signals: [
          ['09:14:22', 'AWS CloudTrail', 'iam:AttachRolePolicy grants AdministratorAccess to role dba-role by user m.ortiz.'],
          ['09:16:05', 'Okta', 'm.ortiz added to group “AWS-Prod-Admins” outside change window.'],
          ['09:19:48', 'CrowdStrike', 'Interactive session on privileged host db-bastion-02 under m.ortiz.'],
        ],
        reasoning:
          'Self-elevation pattern across AWS IAM and Okta group membership, then a privileged host — outside an approved change window. High blast radius → approval-gated.',
        confidence: 0.89,
        attack: ['T1098 Account Manipulation', 'T1078.004 Cloud Accounts'],
        chain: [
          ['Detach AdministratorAccess from dba-role (rollback)', 'pending'],
          ['Remove m.ortiz from AWS-Prod-Admins Okta group', 'pending'],
          ['Freeze m.ortiz sessions pending review', 'pending'],
        ],
        audit: [
          ['09:19:52', 'ai', 'Correlation engine joined 3 signals on identity=m.ortiz (conf 0.89)'],
          ['09:19:55', 'ai', 'Remediation plan synthesized. Rank policy: employee identity freeze = GATED'],
          ['09:19:56', 'ai', 'Approval requested → SOC queue (SLA 15m)'],
        ],
      },
    },
    {
      id: 'INC-4030',
      sev: 'high',
      title: 'Contractor OAuth abuse → S3 exfil pattern',
      source: 'Okta → AWS GuardDuty',
      asset: 'data-lake',
      state: 'progress',
      xdom: true,
      detail: {
        signals: [
          ['07:48:10', 'Okta', 'Anomalous OAuth grant: contractor approves unverified app “SheetSyncPro”.'],
          ['07:59:33', 'AWS GuardDuty', 'Exfiltration:S3/AnomalousBehavior — unusual GetObject volume on data-lake.'],
        ],
        reasoning: 'OAuth consent phishing followed by programmatic S3 reads through the granted token. Join is token lineage, not user IP.',
        confidence: 0.91,
        attack: ['T1550.001 App Access Token', 'T1530 Data from Cloud Storage'],
        chain: [
          ['Revoke OAuth grant + quarantine app in Okta', 'done'],
          ['Apply deny bucket policy for the token’s session tags', 'done'],
          ['Snapshot access logs for IR report', 'progress'],
        ],
        audit: [
          ['07:59:40', 'ai', 'Token-lineage correlation Okta grant ↔ GuardDuty finding (conf 0.91)'],
          ['07:59:44', 'ok', 'Okta: grant revoked, app quarantined'],
          ['07:59:47', 'ok', 'S3: deny policy applied to data-lake'],
        ],
      },
    },
    {
      id: 'INC-4033',
      sev: 'high',
      title: 'Phishing → endpoint → cloud pivot',
      source: 'CrowdStrike → Okta → Prisma',
      asset: 'fin-laptop-registry',
      state: 'awaiting',
      xdom: true,
      detail: {
        signals: [
          ['10:22:18', 'CrowdStrike', 'Malicious macro spawned PowerShell on fin-laptop-registry (blocked).'],
          ['10:24:02', 'Okta', 'MFA push-bombing: 7 pushes in 3 minutes, one approved.'],
          ['10:31:56', 'Prisma Cloud', 'New over-privileged IAM role “temp-fin-audit” created in payments account.'],
        ],
        reasoning: 'Classic phish-to-cloud pivot: endpoint compromise, MFA fatigue, then rogue role persistence. Role deletion is gated.',
        confidence: 0.87,
        attack: ['T1204 User Execution', 'T1621 MFA Request Generation', 'T1136 Create Account'],
        chain: [
          ['Contain host + kill process tree (CrowdStrike)', 'done'],
          ['Deny MFA + revoke user sessions (Okta)', 'done'],
          ['Delete rogue role temp-fin-audit (payments acct)', 'pending'],
        ],
        audit: [
          ['10:32:01', 'ai', '3-tool correlation on user + payments account (conf 0.87)'],
          ['10:32:05', 'ok', 'CrowdStrike: containment complete'],
          ['10:32:08', 'ok', 'Okta: sessions revoked, MFA denied'],
          ['10:32:10', 'ai', 'Role deletion GATED → approval requested'],
        ],
      },
    },
    {
      id: 'INC-4008',
      sev: 'medium',
      title: 'Impossible travel sign-in',
      source: 'Okta',
      asset: 'sales-user',
      state: 'auto',
      detail: {
        signals: [['06:12:44', 'Okta', 'Sign-in from Warsaw 22 min after NYC sign-in, same account, no VPN correlation.']],
        reasoning: 'Geo-velocity violation with no corporate VPN or travel-calendar match. Single-tool threat, low blast radius → auto.',
        confidence: 0.92,
        attack: ['T1078 Valid Accounts'],
        chain: [['Revoke session + force re-auth with MFA', 'done']],
        audit: [
          ['06:12:47', 'ai', 'Classified threat (conf 0.92), Rank: AUTO'],
          ['06:12:49', 'ok', 'Okta: session revoked, MFA re-auth forced'],
        ],
      },
    },
    {
      id: 'INC-4014',
      sev: 'high',
      title: 'Endpoint beacon detected',
      source: 'CrowdStrike Falcon',
      asset: 'eng-laptop-114',
      state: 'auto',
      detail: {
        signals: [['04:18:30', 'CrowdStrike', 'Periodic outbound to uncategorized domain, 60s interval.']],
        reasoning: 'Beacon cadence + unsigned parent process + domain age < 30 days. Host isolation graduated to AUTO.',
        confidence: 0.93,
        attack: ['T1071 C2 Channel'],
        chain: [
          ['Isolate host', 'done'],
          ['Capture triage package', 'done'],
        ],
        audit: [
          ['04:18:34', 'ai', 'Classified threat (conf 0.93), Rank: AUTO'],
          ['04:18:36', 'ok', 'CrowdStrike: host contained'],
        ],
      },
    },
    {
      id: 'INC-4001',
      sev: 'high',
      title: 'Host isolation reverted — false containment',
      source: 'CrowdStrike Falcon',
      asset: 'build-server-07',
      state: 'reverted',
      detail: {
        signals: [['02:10:05', 'CrowdStrike', 'Beacon-like traffic from build-server-07 flagged; host auto-isolated.']],
        reasoning: 'Post-action validation found a new artifact-registry health check. Reverted containment in 94 seconds.',
        confidence: 0.71,
        attack: ['T1071 (reclassified: benign)'],
        chain: [
          ['Isolate host', 'done'],
          ['Validation failed → automatic un-isolate', 'done'],
          ['Allowlist registry health-check pattern', 'done'],
        ],
        audit: [
          ['02:10:09', 'ai', 'Auto-isolated (conf 0.71 — borderline)'],
          ['02:11:20', 'ai', 'Validation: traffic matches new registry deploy manifest'],
          ['02:11:43', 'ok', 'Containment REVERTED, host restored (94s impact)'],
        ],
      },
    },
    {
      id: 'FP-2201',
      sev: 'info',
      title: '“Impossible travel” — corporate VPN egress',
      source: 'Okta',
      asset: 'j.alvarez',
      state: 'suppressed',
      fp: true,
      detail: {
        signals: [['07:20:11', 'Okta', 'Sign-in NYC → Frankfurt within 11 minutes for j.alvarez.']],
        reasoning: 'Frankfurt IP belongs to corporate VPN egress range; calendar shows approved travel. Suppressed — retained.',
        confidence: 0.97,
        attack: [],
        chain: [],
        audit: [
          ['07:20:14', 'ai', 'Matched VPN egress range vpn-fra-01'],
          ['07:20:15', 'ai', 'SUPPRESSED as false positive (conf 0.97)'],
        ],
      },
    },
  ];
}

const FILLER: [string, string, string, C3MockIncident['state'], C3MockIncident['sev']][] = [
  ['S3 bucket policy drift', 'Prisma Cloud', 'backup-vault', 'auto', 'medium'],
  ['Stale access key used', 'AWS CloudTrail', 'ci-runner-legacy', 'auto', 'medium'],
  ['Suspicious admin console login', 'Okta', 'it-admin-2', 'auto', 'medium'],
  ['CloudTrail logging gap', 'AWS', 'audit-account', 'progress', 'medium'],
  ['Container escape attempt blocked', 'CrowdStrike Falcon', 'k8s-node-4', 'auto', 'high'],
  ['Public snapshot detected', 'Prisma Cloud', 'rds-reporting', 'auto', 'high'],
  ['Dormant account reactivated', 'Okta', 'vendor-acct-11', 'awaiting', 'medium'],
  ['Ransomware canary touched', 'CrowdStrike Falcon', 'file-share-02', 'progress', 'high'],
  ['Password spray detected', 'Okta', 'all-tenants', 'auto', 'low'],
  ['GuardDuty recon finding', 'AWS GuardDuty', 'prod-vpc', 'auto', 'low'],
  ['AssumeRole from new ASN', 'AWS CloudTrail', 'data-acct', 'progress', 'medium'],
  ['New device enrollment anomaly', 'Okta', 'exec-cohort', 'awaiting', 'medium'],
];

export function makeC3Incidents(): C3MockIncident[] {
  const I = heroIncidents();
  FILLER.forEach((f, i) => {
    I.push({
      id: `INC-39${50 + i}`,
      sev: f[4],
      title: f[0],
      source: f[1],
      asset: f[2],
      state: f[3],
      detail: {
        signals: [['—', f[1], `${f[0]} on ${f[2]}.`]],
        reasoning: `Single-tool detection classified by the DNN stage; remediation synthesized via GenAI + RAG against the ${f[1]} runbook corpus.`,
        confidence: 0.86,
        attack: ['Mapped in IR report'],
        chain: [
          [
            f[3] === 'awaiting' ? 'Remediation pending approval' : 'Standard runbook remediation',
            f[3] === 'awaiting' ? 'pending' : f[3] === 'progress' ? 'progress' : 'done',
          ],
        ],
        audit: [
          ['—', 'ai', 'Classified (conf 0.86), routed per Agent Rank'],
          ['—', f[3] === 'awaiting' ? 'ai' : 'ok', f[3] === 'awaiting' ? 'Approval requested' : 'Remediation executed and validated'],
        ],
      },
    });
  });
  return I;
}

export function buildDetailForLiveIncident(opts: {
  id: string;
  title: string;
  source: string;
  description: string;
  status: string;
}): C3MockIncident['detail'] {
  const state =
    opts.status === 'remediated' ? 'done' : opts.status === 'analyzing' ? 'progress' : 'pending';
  return {
    signals: [['—', opts.source, opts.description || opts.title]],
    reasoning: `Live threat event “${opts.title}” from ${opts.source}. Open the remediation workspace for full AI analysis, S3 evidence, and the remediation agent.`,
    confidence: 0.8,
    attack: [],
    chain: [['Investigate & remediate in workspace', state]],
    audit: [['—', 'ai', 'Loaded from live threat rings']],
  };
}

export const STATE_META: Record<
  string,
  { label: string; cls: string; dot: string }
> = {
  auto: { label: 'Remediated', cls: 'text-c3-green border-c3-green', dot: 'bg-c3-green' },
  progress: { label: 'In progress', cls: 'text-c3-blue border-c3-blue', dot: 'bg-c3-blue' },
  awaiting: { label: 'Awaiting decision', cls: 'text-c3-amber border-c3-amber', dot: 'bg-c3-amber' },
  reverted: { label: 'Reverted', cls: 'text-c3-red border-c3-red', dot: 'bg-c3-red' },
  suppressed: { label: 'Suppressed', cls: 'text-c3-purple border-c3-purple', dot: 'bg-c3-purple' },
};
